import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ZoeaLogo from '@/components/ZoeaLogo';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, Check } from 'lucide-react';


const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [searchParams] = useSearchParams();
  const plan = ['starter', 'professional', 'enterprise'].includes(searchParams.get('plan'))
    ? searchParams.get('plan')
    : 'starter';
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name || !email || !password) {
      toast.error('Vul alle verplichte velden in');
      return;
    }

    if (password.length < 6) {
      toast.error('Wachtwoord moet minimaal 6 karakters zijn');
      return;
    }

    setLoading(true);
    try {
      await register(email, password, name, organizationName, plan);
      toast.success('Account aangemaakt! Welkom bij Zoea.');
      navigate('/dashboard');
    } catch (error) {
      console.error('Register error:', error);
      toast.error(error.response?.data?.detail || 'Registratie mislukt');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    "Boekingen beheren",
    "Drag-and-drop kalender",
    "Team management"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30 flex">
      {/* Left Side - Visual */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-teal-600 to-emerald-600 items-center justify-center p-12">
        <div className="max-w-lg text-white">
          <h2 className="text-3xl font-bold mb-4 font-['Manrope']">
            Start vandaag nog met slimmer plannen
          </h2>
          <p className="text-teal-100 text-lg mb-8">
            Maak in minder dan 2 minuten een account aan en ontdek hoe eenvoudig reserveringsbeheer kan zijn.
          </p>
          
          <ul className="space-y-4">
            {benefits.map((benefit, index) => (
              <li key={index} className="flex items-center gap-3">
                <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4" />
                </div>
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      
      {/* Right Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="w-full max-w-sm mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span>Terug naar home</span>
          </Link>
          
          <div className="flex items-center gap-3 mb-8">
            <ZoeaLogo className="h-12" />
          </div>
          
          <h1 className="text-2xl font-bold text-slate-900 mb-2 font-['Manrope']">Account aanmaken</h1>
          <p className="text-slate-600 mb-4">Vul je gegevens in om te starten</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Volledige naam *</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jan Jansen"
                className="h-12 rounded-xl border-slate-200 focus:border-teal-500 focus:ring-teal-500/20"
                data-testid="register-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">E-mailadres *</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="naam@voorbeeld.nl"
                className="h-12 rounded-xl border-slate-200 focus:border-teal-500 focus:ring-teal-500/20"
                data-testid="register-email"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="organization">Organisatienaam</Label>
              <Input
                id="organization"
                type="text"
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                placeholder="Mijn Bedrijf (optioneel)"
                className="h-12 rounded-xl border-slate-200 focus:border-teal-500 focus:ring-teal-500/20"
                data-testid="register-organization"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Wachtwoord *</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimaal 6 karakters"
                  className="h-12 rounded-xl border-slate-200 focus:border-teal-500 focus:ring-teal-500/20 pr-12"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-medium mt-2"
              data-testid="register-submit"
            >
              {loading ? 'Account aanmaken...' : 'Account aanmaken'}
            </Button>
          </form>
          
          <p className="text-center text-slate-500 text-sm mt-6">
            Door te registreren ga je akkoord met onze{' '}
            <a href="#" className="text-teal-600 hover:underline">Voorwaarden</a>
            {' '}en{' '}
            <a href="#" className="text-teal-600 hover:underline">Privacy Policy</a>
          </p>
          
          <p className="text-center text-slate-600 mt-4">
            Al een account?{' '}
            <Link to="/login" className="text-teal-600 hover:text-teal-700 font-medium">
              Inloggen
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
